/**
 * ISF (Interactive Shader Format) loader (ADR-0005): parses the JSON header, maps inputs to our
 * ParamDefs, translates ISF/GLSL 1.x idioms to GLSL ES 3.00, and validates against our shader
 * safety rules (ADR-0007). Pure, so it is unit-tested in Node.
 *
 * Supported: float, bool, long, event, color, point2D, audio, audioFFT inputs; multi-pass with
 * TARGET / PERSISTENT / FLOAT / WIDTH / HEIGHT; the standard ISF uniforms and IMG_* macros.
 * Not supported: image inputs (there is no media source for them yet).
 */
import type { ParamDef } from '../../../shared/params';

export interface IsfInput {
  name: string;
  type: 'float' | 'bool' | 'long' | 'event' | 'color' | 'point2D' | 'audio' | 'audioFFT';
  /** Long inputs: the integer VALUES the select options map to. */
  values?: number[];
}

export interface IsfPass {
  target: string | null;
  persistent: boolean;
  float: boolean;
  width: string | null;
  height: string | null;
}

export interface ParsedIsf {
  name: string;
  description: string;
  credit: string;
  categories: string[];
  inputs: IsfInput[];
  params: ParamDef[];
  passes: IsfPass[];
  /** The shader body, translated to GLSL ES 3.00 (without our prelude). */
  body: string;
}

export class IsfError extends Error {}

const MAX_SOURCE = 64 * 1024;
const MAX_PASSES = 8;

export function parseIsf(source: string, fallbackName = 'ISF shader'): ParsedIsf {
  if (source.length > MAX_SOURCE) throw new IsfError('Shader is too large (64 KB max).');
  const m = /^\s*\/\*([\s\S]*?)\*\//.exec(source);
  if (!m) throw new IsfError('Not an ISF shader: missing the /*{ … }*/ JSON header.');
  let header: Record<string, unknown>;
  try {
    header = JSON.parse(m[1]!.trim()) as Record<string, unknown>;
  } catch (err) {
    throw new IsfError(`Invalid ISF header JSON: ${(err as Error).message}`);
  }
  const body = source.slice(m[0].length);
  checkSafety(body);

  const inputs: IsfInput[] = [];
  const params: ParamDef[] = [];
  for (const raw of (header.INPUTS as Record<string, unknown>[] | undefined) ?? []) {
    const name = String(raw.NAME ?? '');
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) throw new IsfError(`Invalid input name “${name}”.`);
    const label = String(raw.LABEL ?? name);
    const type = String(raw.TYPE ?? '');
    const num = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d);
    switch (type) {
      case 'float': {
        const min = num(raw.MIN, 0);
        const max = num(raw.MAX, 1);
        params.push({
          id: name,
          label,
          type: 'float',
          min,
          max: max > min ? max : min + 1,
          default: num(raw.DEFAULT, min),
        });
        inputs.push({ name, type: 'float' });
        break;
      }
      case 'bool':
      case 'event':
        params.push({
          id: name,
          label,
          type: 'float',
          min: 0,
          max: 1,
          step: 1,
          default: raw.DEFAULT ? 1 : 0,
          control: 'toggle',
        });
        inputs.push({ name, type });
        break;
      case 'long': {
        const values = Array.isArray(raw.VALUES)
          ? (raw.VALUES as unknown[]).map((v) => num(v, 0))
          : [];
        const labels = Array.isArray(raw.LABELS)
          ? (raw.LABELS as unknown[]).map(String)
          : values.map(String);
        if (values.length === 0) throw new IsfError(`Input “${name}” (long) needs VALUES.`);
        const defIdx = Math.max(0, values.indexOf(num(raw.DEFAULT, values[0]!)));
        params.push({
          id: name,
          label,
          type: 'float',
          min: 0,
          max: values.length - 1,
          step: 1,
          default: defIdx,
          control: 'select',
          options: labels,
        });
        inputs.push({ name, type: 'long', values });
        break;
      }
      case 'color': {
        const d = Array.isArray(raw.DEFAULT)
          ? (raw.DEFAULT as unknown[]).map((v) => num(v, 1))
          : [1, 1, 1, 1];
        ['r', 'g', 'b'].forEach((c, i) =>
          params.push({
            id: `${name}.${c}`,
            label: `${label} ${c.toUpperCase()}`,
            type: 'float',
            min: 0,
            max: 1,
            default: d[i] ?? 1,
          }),
        );
        inputs.push({ name, type: 'color' });
        break;
      }
      case 'point2D': {
        const d = Array.isArray(raw.DEFAULT)
          ? (raw.DEFAULT as unknown[]).map((v) => num(v, 0))
          : [0, 0];
        const mn = Array.isArray(raw.MIN) ? (raw.MIN as unknown[]).map((v) => num(v, 0)) : [0, 0];
        const mx = Array.isArray(raw.MAX) ? (raw.MAX as unknown[]).map((v) => num(v, 1)) : [1, 1];
        ['x', 'y'].forEach((c, i) =>
          params.push({
            id: `${name}.${c}`,
            label: `${label} ${c}`,
            type: 'float',
            min: mn[i] ?? 0,
            max: mx[i] ?? 1,
            default: d[i] ?? 0,
          }),
        );
        inputs.push({ name, type: 'point2D' });
        break;
      }
      case 'audio':
      case 'audioFFT':
        inputs.push({ name, type });
        break;
      case 'image':
        throw new IsfError(`Input “${name}” is an image; image inputs aren’t supported yet.`);
      default:
        throw new IsfError(`Unsupported input type “${type}”.`);
    }
  }

  const rawPasses =
    Array.isArray(header.PASSES) && header.PASSES.length
      ? (header.PASSES as Record<string, unknown>[])
      : [{}];
  if (rawPasses.length > MAX_PASSES) throw new IsfError(`Too many passes (max ${MAX_PASSES}).`);
  const passes: IsfPass[] = rawPasses.map((p) => {
    const target = typeof p.TARGET === 'string' && p.TARGET ? p.TARGET : null;
    if (target && !/^[A-Za-z_][A-Za-z0-9_]*$/.test(target))
      throw new IsfError(`Invalid pass target “${target}”.`);
    return {
      target,
      persistent: p.PERSISTENT === true || p.PERSISTENT === 1 || p.PERSISTENT === 'true',
      float: p.FLOAT === true || p.FLOAT === 1 || p.FLOAT === 'true',
      width: p.WIDTH !== undefined ? String(p.WIDTH) : null,
      height: p.HEIGHT !== undefined ? String(p.HEIGHT) : null,
    };
  });

  return {
    name: typeof header.NAME === 'string' ? header.NAME : fallbackName,
    description: typeof header.DESCRIPTION === 'string' ? header.DESCRIPTION : '',
    credit: typeof header.CREDIT === 'string' ? header.CREDIT : '',
    categories: Array.isArray(header.CATEGORIES)
      ? (header.CATEGORIES as unknown[]).map(String)
      : [],
    inputs,
    params,
    passes,
    body: translateBody(body),
  };
}

/** Reject constructs that can hang the GPU (repeated hangs get WebGL blocked for the site). */
function checkSafety(body: string): void {
  const code = body.replace(/\/\*[\s\S]*?\*\//g, '').replace(/\/\/.*$/gm, '');
  if (/\bwhile\s*\(/.test(code))
    throw new IsfError(
      'while-loops aren’t allowed (they can hang the GPU). Use a for-loop with a constant bound.',
    );
  const forRe = /\bfor\s*\(([^;]*);([^;]*);/g;
  let m: RegExpExecArray | null;
  while ((m = forRe.exec(code))) {
    const cond = m[2]!;
    const bound = /[<>]=?\s*([0-9.]+)\s*$/.exec(cond.trim());
    if (!bound)
      throw new IsfError(`Loop condition “${cond.trim()}” must compare against a constant number.`);
    if (Number(bound[1]) > 1024)
      throw new IsfError(`Loop bound ${bound[1]} is too large (max 1024).`);
  }
}

/** ISF/GLSL 1.x → GLSL ES 3.00 fragment body. */
export function translateBody(body: string): string {
  return body
    .replace(/^\s*#version.*$/gm, '')
    .replace(/^\s*precision\s+\w+\s+\w+\s*;/gm, '')
    .replace(/\bvarying\b/g, 'in')
    .replace(/\btexture2D\b/g, 'texture')
    .replace(/\btexture2DRect\b/g, 'texture');
}

/** Uniform declarations and macros an ISF body expects, plus our audio/beat extensions. */
export function isfPrelude(parsed: ParsedIsf): string {
  const lines = [
    '#version 300 es',
    'precision highp float;',
    'precision highp int;',
    'precision highp sampler2D;',
    'in vec2 vUv;',
    'out vec4 isf_FragColor;',
    '#define gl_FragColor isf_FragColor',
    '#define isf_FragNormCoord vUv',
    'uniform float TIME;',
    'uniform float TIMEDELTA;',
    'uniform vec2 RENDERSIZE;',
    'uniform int FRAMEINDEX;',
    'uniform int PASSINDEX;',
    'uniform vec4 DATE;',
    '#define IMG_NORM_PIXEL(img, c) texture(img, c)',
    '#define IMG_PIXEL(img, c) texture(img, (c) / vec2(textureSize(img, 0)))',
    '#define IMG_THIS_PIXEL(img) texture(img, vUv)',
    '#define IMG_THIS_NORM_PIXEL(img) texture(img, vUv)',
    '#define IMG_SIZE(img) vec2(textureSize(img, 0))',
    // Extensions: audio features, beat, and the scene palette.
    'uniform float GLOW_BASS, GLOW_MID, GLOW_TREBLE, GLOW_LEVEL, GLOW_BEAT_PHASE, GLOW_BEAT_PULSE, GLOW_BAR_PHASE, GLOW_BPM;',
    'uniform float GLOW_ONSET, GLOW_ENERGY, GLOW_PHRASE_PHASE, GLOW_BEAT_IN_BAR;',
    'uniform sampler2D GLOW_PALETTE;',
    'uniform sampler2D GLOW_SPECTRUM;',
    '// Log-frequency spectrum, x 0 (bass) … 1 (treble), values 0..1.',
    'float glowSpectrum(float x) { return texture(GLOW_SPECTRUM, vec2(clamp(x, 0.0, 1.0), 0.5)).r; }',
    'uniform float GLOW_PALETTE_SHIFT;',
    'vec3 glowPalette(float t) { return texture(GLOW_PALETTE, vec2(fract(t + GLOW_PALETTE_SHIFT), 0.5)).rgb; }',
  ];
  for (const i of parsed.inputs) {
    switch (i.type) {
      case 'float':
        lines.push(`uniform float ${i.name};`);
        break;
      case 'bool':
      case 'event':
        lines.push(`uniform bool ${i.name};`);
        break;
      case 'long':
        lines.push(`uniform int ${i.name};`);
        break;
      case 'color':
        lines.push(`uniform vec4 ${i.name};`);
        break;
      case 'point2D':
        lines.push(`uniform vec2 ${i.name};`);
        break;
      case 'audio':
      case 'audioFFT':
        lines.push(`uniform sampler2D ${i.name};`);
        break;
    }
  }
  const targets = [...new Set(parsed.passes.map((p) => p.target).filter((t): t is string => !!t))];
  for (const t of targets) lines.push(`uniform sampler2D ${t};`);
  return `${lines.join('\n')}\n#line 1\n`;
}

/**
 * Evaluate a pass WIDTH/HEIGHT expression like "$WIDTH/2.0" or "floor($HEIGHT * scale)".
 * A small recursive-descent parser: numbers, + − × ÷, parentheses, unary minus, and
 * floor/ceil/round/min/max/abs/pow/sqrt. No eval().
 */
export function evalSizeExpr(expr: string, vars: Record<string, number>): number {
  const src = expr.replace(/\$([A-Za-z_][A-Za-z0-9_]*)/g, (_, n: string) => {
    const v = vars[n];
    if (v === undefined) throw new IsfError(`Unknown variable $${n} in “${expr}”.`);
    return String(v);
  });
  let i = 0;
  const peek = () => src[i] ?? '';
  const skip = () => {
    while (/\s/.test(peek())) i++;
  };
  const fns: Record<string, (...a: number[]) => number> = {
    floor: Math.floor,
    ceil: Math.ceil,
    round: Math.round,
    min: Math.min,
    max: Math.max,
    abs: Math.abs,
    pow: Math.pow,
    sqrt: Math.sqrt,
  };
  function primary(): number {
    skip();
    if (peek() === '(') {
      i++;
      const v = sum();
      skip();
      if (peek() !== ')') throw new IsfError(`Expected ) in “${expr}”.`);
      i++;
      return v;
    }
    if (peek() === '-') {
      i++;
      return -primary();
    }
    const num = /^\d*\.?\d+(?:e[-+]?\d+)?/i.exec(src.slice(i));
    if (num) {
      i += num[0].length;
      return Number(num[0]);
    }
    const id = /^[A-Za-z_]\w*/.exec(src.slice(i));
    if (id && fns[id[0]]) {
      i += id[0].length;
      skip();
      if (peek() !== '(') throw new IsfError(`Expected ( after ${id[0]}.`);
      i++;
      const args = [sum()];
      skip();
      while (peek() === ',') {
        i++;
        args.push(sum());
        skip();
      }
      if (peek() !== ')') throw new IsfError(`Expected ) in “${expr}”.`);
      i++;
      return fns[id[0]]!(...args);
    }
    throw new IsfError(`Can’t evaluate “${expr}”.`);
  }
  function product(): number {
    let v = primary();
    for (;;) {
      skip();
      const op = peek();
      if (op !== '*' && op !== '/') return v;
      i++;
      const r = primary();
      v = op === '*' ? v * r : v / r;
    }
  }
  function sum(): number {
    let v = product();
    for (;;) {
      skip();
      const op = peek();
      if (op !== '+' && op !== '-') return v;
      i++;
      const r = product();
      v = op === '+' ? v + r : v - r;
    }
  }
  const v = sum();
  skip();
  if (i < src.length) throw new IsfError(`Unexpected “${src.slice(i)}” in “${expr}”.`);
  if (!Number.isFinite(v)) throw new IsfError(`“${expr}” is not a finite number.`);
  return v;
}

/** Stable id for an imported shader. */
export function isfId(name: string, source: string): string {
  let h = 2166136261;
  for (let k = 0; k < source.length; k++) h = Math.imul(h ^ source.charCodeAt(k), 16777619);
  return `isf-${name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .slice(0, 32)}-${(h >>> 0).toString(36)}`;
}
